/**
 * MonitorEditPage - Monitor edit form page
 *
 * Provides a form to edit an existing directory monitor configuration.
 */

import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { useMonitor, useUpdateMonitor } from '@/features/monitors/hooks/useMonitors';
import { DatasetCombobox } from '@/components/datasets/DatasetCombobox';
import { useSchemas } from '@/features/schemas/hooks/useSchemas';
import { useToast } from '@/hooks/useToast';
import type {
  UpdateMonitorRequest,
  MonitorStatus,
  MonitorType,
  ScanFrequency,
} from '@/features/monitors/types';

/**
 * MonitorEditPage Component
 *
 * Allows editing of an existing directory monitor configuration.
 *
 * @example
 * ```tsx
 * <Route path="/monitors/:id/edit" element={<MonitorEditPage />} />
 * ```
 */
export const MonitorEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch monitor data
  const { data: monitor, isLoading, error } = useMonitor(id || '', { enabled: !!id });
  const { data: schemasData } = useSchemas();

  // Mutations
  const updateMonitor = useUpdateMonitor();

  // Form state. Lazy-initialized from `monitor` in case it's already
  // available on the very first render (e.g. a warm query cache), so the
  // render-phase sync below — which only reacts to `monitor` *changing* —
  // doesn't miss the case where it never changes because it was there from
  // the start.
  const [formData, setFormData] = useState<UpdateMonitorRequest>(() =>
    monitor
      ? {
          name: monitor.name,
          description: monitor.description || '',
          directory_path: monitor.directory_path,
          scan_frequency: monitor.scan_frequency,
          monitor_type: monitor.monitor_type,
          status: monitor.status,
          dataset_id: monitor.dataset_id || undefined,
          default_schema_id: monitor.default_schema_id || undefined,
          auto_ingest: monitor.auto_ingest,
          trigger_delay_seconds: monitor.trigger_delay_seconds || 5,
          batch_size: monitor.batch_size || 10,
        }
      : {}
  );
  const [includePatterns, setIncludePatterns] = useState<string>(() =>
    monitor ? monitor.file_patterns.include_patterns.join('\n') : ''
  );
  const [excludePatterns, setExcludePatterns] = useState<string>(() =>
    monitor ? monitor.file_patterns.exclude_patterns.join('\n') : ''
  );
  const [recursive, setRecursive] = useState(() => monitor?.file_patterns.recursive || false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  /**
   * Re-initialize form with monitor data if it arrives (or changes) after
   * mount.
   *
   * Adjusted during render (not an effect) via a previous-value mirror of
   * `monitor` itself: the query hook returns a referentially stable object
   * (TanStack Query's structural sharing keeps the same reference across
   * renders when the data is unchanged), so this is safe to mirror directly.
   * Converges on the very next render, so this fires exactly once per
   * `monitor` identity change (matching the original effect's [monitor] dep).
   */
  const [prevMonitor, setPrevMonitor] = useState(monitor);
  if (monitor !== prevMonitor) {
    setPrevMonitor(monitor);
    if (monitor) {
      setFormData({
        name: monitor.name,
        description: monitor.description || '',
        directory_path: monitor.directory_path,
        scan_frequency: monitor.scan_frequency,
        monitor_type: monitor.monitor_type,
        status: monitor.status,
        dataset_id: monitor.dataset_id || undefined,
        default_schema_id: monitor.default_schema_id || undefined,
        auto_ingest: monitor.auto_ingest,
        trigger_delay_seconds: monitor.trigger_delay_seconds || 5,
        batch_size: monitor.batch_size || 10,
      });
      setIncludePatterns(monitor.file_patterns.include_patterns.join('\n'));
      setExcludePatterns(monitor.file_patterns.exclude_patterns.join('\n'));
      setRecursive(monitor.file_patterns.recursive || false);
    }
  }

  /**
   * Handle form field changes
   */
  const handleFieldChange = useCallback(
    (field: keyof UpdateMonitorRequest, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear validation error for this field
      if (validationErrors[field]) {
        setValidationErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    },
    [validationErrors]
  );

  /**
   * Validate form data
   */
  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length === 0) {
      errors.name = 'Name is required';
    }

    if (includePatterns.trim().length === 0) {
      errors.includePatterns = 'At least one include pattern is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, includePatterns]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validateForm() || !id) return;

      try {
        const updateData: UpdateMonitorRequest = {
          ...formData,
          file_patterns: {
            include_patterns: includePatterns
              .split('\n')
              .map((p) => p.trim())
              .filter(Boolean),
            exclude_patterns: excludePatterns
              .split('\n')
              .map((p) => p.trim())
              .filter(Boolean),
            recursive,
          },
        };

        await updateMonitor.mutateAsync({
          id,
          data: updateData,
        });

        toast({
          title: 'Monitor Updated',
          description: `Monitor "${formData.name}" has been updated successfully`,
        });

        navigate(`/monitors/${id}`);
      } catch (error: any) {
        console.error('Failed to update monitor:', error);
        toast({
          title: 'Update Failed',
          description: error.response?.data?.detail || 'Failed to update monitor',
          variant: 'destructive',
        });
      }
    },
    [
      validateForm,
      id,
      formData,
      includePatterns,
      excludePatterns,
      recursive,
      updateMonitor,
      toast,
      navigate,
    ]
  );

  /**
   * Handle cancel
   */
  const handleCancel = useCallback(() => {
    navigate(`/monitors/${id}`);
  }, [navigate, id]);

  /**
   * Loading state
   */
  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  /**
   * Error state
   */
  if (error || !monitor) {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error?.message || 'Monitor not found'}
            <Button variant="link" onClick={() => navigate('/monitors')} className="ml-2">
              Back to Monitors
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(`/monitors/${id}`)} className="mb-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Monitor Details
        </Button>

        <div>
          <h1 className="text-3xl font-bold">Edit Monitor</h1>
          <p className="text-muted-foreground">Update monitor configuration and settings</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Monitor name and description</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Monitor Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="My Directory Monitor"
                  className={validationErrors.name ? 'border-destructive' : ''}
                />
                {validationErrors.name && (
                  <p className="text-sm text-destructive">{validationErrors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Describe what this monitor does..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Directory Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Directory Configuration</CardTitle>
              <CardDescription>Directory path and file patterns</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="directory_path">Directory Path</Label>
                <Input
                  id="directory_path"
                  value={formData.directory_path || ''}
                  onChange={(e) => handleFieldChange('directory_path', e.target.value)}
                  placeholder="/data/incoming"
                  className="font-mono"
                  readOnly
                  disabled
                />
                <p className="text-sm text-muted-foreground">
                  Note: Directory path cannot be changed after creation
                </p>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="includePatterns">
                  Include Patterns <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="includePatterns"
                  value={includePatterns}
                  onChange={(e) => setIncludePatterns(e.target.value)}
                  placeholder="*.parquet&#10;*.csv&#10;data_*.json"
                  rows={4}
                  className={`font-mono ${validationErrors.includePatterns ? 'border-destructive' : ''}`}
                />
                {validationErrors.includePatterns && (
                  <p className="text-sm text-destructive">{validationErrors.includePatterns}</p>
                )}
                <p className="text-sm text-muted-foreground">One pattern per line</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="excludePatterns">Exclude Patterns</Label>
                <Textarea
                  id="excludePatterns"
                  value={excludePatterns}
                  onChange={(e) => setExcludePatterns(e.target.value)}
                  placeholder="*_temp.*&#10;*_backup.*&#10;*.tmp"
                  rows={3}
                  className="font-mono"
                />
                <p className="text-sm text-muted-foreground">One pattern per line (optional)</p>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="recursive"
                  checked={recursive}
                  onCheckedChange={(checked) => setRecursive(checked === true)}
                />
                <Label htmlFor="recursive" className="font-normal cursor-pointer">
                  Scan subdirectories recursively
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Scheduling Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Scheduling Configuration</CardTitle>
              <CardDescription>Monitor type and scan frequency</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="monitor_type">Monitor Type</Label>
                  <Select
                    value={formData.monitor_type || ''}
                    onValueChange={(value) =>
                      handleFieldChange('monitor_type', value as MonitorType)
                    }
                  >
                    <SelectTrigger id="monitor_type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="continuous">Continuous</SelectItem>
                      <SelectItem value="event-driven">Event-Driven</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="scan_frequency">Scan Frequency</Label>
                  <Select
                    value={formData.scan_frequency || ''}
                    onValueChange={(value) =>
                      handleFieldChange('scan_frequency', value as ScanFrequency)
                    }
                  >
                    <SelectTrigger id="scan_frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="hourly">Hourly</SelectItem>
                      <SelectItem value="every-15-min">Every 15 Minutes</SelectItem>
                      <SelectItem value="continuous">Continuous</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Monitor Status</Label>
                <Select
                  value={formData.status || ''}
                  onValueChange={(value) => handleFieldChange('status', value as MonitorStatus)}
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="disabled">Disabled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Target Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Target Configuration</CardTitle>
              <CardDescription>Dataset and schema configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dataset_id">Target Dataset</Label>
                <DatasetCombobox
                  id="dataset_id"
                  value={formData.dataset_id || null}
                  placeholder="Select dataset..."
                  emptyOptionLabel="No dataset"
                  onChange={(datasetId) => handleFieldChange('dataset_id', datasetId)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="default_schema_id">Default Schema</Label>
                <Select
                  value={formData.default_schema_id || 'none'}
                  onValueChange={(value) =>
                    handleFieldChange('default_schema_id', value === 'none' ? null : value)
                  }
                >
                  <SelectTrigger id="default_schema_id">
                    <SelectValue placeholder="Select schema..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No schema</SelectItem>
                    {schemasData?.items
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((schema) => (
                        <SelectItem key={schema.id} value={schema.id}>
                          {schema.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="auto_ingest"
                  checked={formData.auto_ingest}
                  onCheckedChange={(checked) => handleFieldChange('auto_ingest', checked === true)}
                />
                <Label htmlFor="auto_ingest" className="font-normal cursor-pointer">
                  Automatically trigger ingestion for discovered files
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Advanced Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Advanced Settings</CardTitle>
              <CardDescription>Optional configuration parameters</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="trigger_delay_seconds">Trigger Delay (seconds)</Label>
                  <Input
                    id="trigger_delay_seconds"
                    type="number"
                    min="0"
                    value={formData.trigger_delay_seconds || 5}
                    onChange={(e) =>
                      handleFieldChange('trigger_delay_seconds', parseInt(e.target.value) || 5)
                    }
                  />
                  <p className="text-sm text-muted-foreground">
                    Delay before triggering ingestion after file discovery
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="batch_size">Batch Size</Label>
                  <Input
                    id="batch_size"
                    type="number"
                    min="1"
                    value={formData.batch_size || 10}
                    onChange={(e) =>
                      handleFieldChange('batch_size', parseInt(e.target.value) || 10)
                    }
                  />
                  <p className="text-sm text-muted-foreground">
                    Maximum files to process in a single batch
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button type="submit" disabled={updateMonitor.isPending}>
              {updateMonitor.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
